import * as React from 'react';
import { Button, ButtonProps } from '@mui/material';

/** Named design-system button intents. */
export type HfButtonIntent = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'warning';

export interface HfButtonProps extends Omit<ButtonProps, 'variant' | 'color'> {
    /** Named design-system intent. Defaults to `primary`. */
    intent?: HfButtonIntent;
}

const INTENT_TO_MUI: Record<HfButtonIntent, { variant: ButtonProps['variant']; color: ButtonProps['color'] }> = {
    primary:   { variant: 'contained', color: 'primary' },
    secondary: { variant: 'outlined',  color: 'primary' },
    ghost:     { variant: 'text',      color: 'primary' },
    danger:    { variant: 'contained', color: 'error' },
    success:   { variant: 'contained', color: 'success' },
    warning:   { variant: 'contained', color: 'warning' }
};

/**
 * Design-system button wrapper around MUI Button.
 *
 * Translates the design-system intent vocabulary
 * (primary / secondary / ghost / danger / success / warning) into the
 * MUI `variant` + `color` pair, picking up the theme overrides defined
 * in `hfMuiTheme.ts`. Consumers that need raw MUI semantics can keep
 * using `<Button>` directly; new code should prefer this wrapper.
 */
export const HfButton = React.forwardRef<HTMLButtonElement, HfButtonProps>(function HfButton(
    { intent = 'primary', ...rest },
    ref
) {
    const mapping = INTENT_TO_MUI[intent];
    return (
        <Button
            ref={ref}
            variant={mapping.variant}
            color={mapping.color}
            {...rest}
        />
    );
});

export default HfButton;
